import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { addSearchedService } from 'bl/services/addSearchedService.bl';
import { fromServicesToFlattened } from 'bl/services/calculateServicesPriceAndTimeRanges.bl';
import Button from 'components/form/Button.component';
import { useTheme } from 'providers/Theme.provider';
import { fromPropsToPath } from 'utils/converters/props/path.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';

import CategoryItem from 'booking/components/common/CategoryItem';
import FrequentlyBtnComponent from 'booking/components/common/FrequentlyBtnComponent';
import SearchSelectBar from 'booking/components/common/SearchSelectBar';

import { addService, pickCategory } from '../actions';

import ServiceItem from './common/ServiceItem';

import CN from './AddAnotherService.module.scss';

const L = logger('AddAnotherService');

const AddAnotherService = (props) => {
  const { client, services, history, addedServices, addService, location, pickCategory } = props;
  const nav = navFor('AddAnotherService', props);
  const path = fromPropsToPath(props);
  const isNotEdit = path.indexOf('edit');

  const { isDarkMode } = useTheme();

  const [isSearching, setIsSearching] = useState(false);

  const handleGoBack = useCallback(() => void nav.back(), [nav]);
  const handleSearching = useCallback((value) => void setIsSearching(value), []);

  const pick = useCallback(
    (category) => {
      pickCategory(category);
      // TODO: rework the entire URL pathname checking complexity into something simpler
      nav.push(`services/${encodeURI(category.name)}/addanotherservice`);
    },
    [nav, pickCategory],
  );

  const handleFrequentClick = useCallback(
    () =>
      void nav.push(
        `${path
          .split('/')
          .filter((str) => str !== 'services')
          .join('/')}/frequently-booked`,
      ),
    [nav, path],
  );

  const handleSelectedSearchedItem = useCallback(
    (service) => {
      addSearchedService({ service, client, addedServices, addService, location, history });
      nav.push(`services`);
    },
    [addService, addedServices, client, history, location, nav],
  );

  const flattenedServices = fromServicesToFlattened(services);

  L.debug('()', { isNotEdit, addedServices });

  return (
    <div
      data-bem="AddAnotherService"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.header}>
        <Button variant="empty" onClick={handleGoBack}>
          <div className={CN.arrow} />
        </Button>
        <div className={CN.title}>Add another service</div>
      </div>
      <div className={CN.content}>
        <SearchSelectBar
          options={flattenedServices}
          onSelect={handleSelectedSearchedItem}
          onStartSearching={handleSearching}
          placeholder={`Search from ${flattenedServices.length} services`}
          renderOption
        >
          {(value, onSelect) => <ServiceItem key={value.id} service={value} onClickService={() => onSelect(value)} />}
        </SearchSelectBar>

        {!isSearching && client && !client.has_no_appointments && (
          <FrequentlyBtnComponent onClickFrequentBtn={handleFrequentClick} />
        )}

        <div data-bem="AddAnotherService__service-list">
          {!isSearching && (
            <div className={CN.space}>
              {services.map((service) => (
                <CategoryItem
                  key={service.id}
                  categoryName={service.name}
                  color={service.color}
                  onAddServiceCategory={() => void pick(service)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={CN.footer}>
        <Button variant="primaryForce" onClick={handleGoBack}>
          <span className={CN.buttonSpace}>SAVE CHANGES</span>
        </Button>
      </div>
    </div>
  );
};

export const mapDispatchToProps = (dispatch) => bindActionCreators({ pickCategory, addService }, dispatch);

const mapStateToProps = (state) => ({
  addedServices: state?.booking?.addedServices,
  clientId: state?.booking?.client?.id ?? undefined,
  isEditBooking: state.booking.isEditBooking,
  services: state?.currentSalon?.services,
  preferenceList: state?.booking?.preferenceList,
});

const AddAnotherServiceHoc = connect(mapStateToProps, mapDispatchToProps)(AddAnotherService);

export default AddAnotherServiceHoc;
